// =============================================================================
// staticSite/cfAccountAllocator — ktore konto Cloudflare ma dostac projekt Pages.
//
// Limit CF: 100 projektow Pages na konto. Panel ma wiele kont (cloudflare_accounts).
// Wybor: konto, ktore ma ZONE tej domeny (custom domain aktywuje sie od razu),
// a jesli nie — konto z najmniejsza liczba projektow. Odrzucamy >= 95 projektow.
// =============================================================================

import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";
import { listAccounts, listZones, type CfZone } from "@/services/cloudflareService";
import { slugify } from "@/lib/slugify";
import { listPagesProjects } from "./cfPagesApi";

export const CF_PAGES_PROJECTS_SOFT_LIMIT = 95;

export interface CfAccountLike {
  id: string;
  label: string;
  api_token: string;
  account_id?: string | null;
}

export interface AccountCandidate {
  account: CfAccountLike;
  /** CF account id (32 hex) — z DB albo z GET /accounts */
  accountId: string;
  projectCount: number;
  ownsZone: boolean;
  zone: CfZone | null;
  error: string | null;
}

/** Nazwa projektu Pages: slug domeny + 6 znakow hasha (unikalna, <= 58 znakow, [a-z0-9-]). */
export function projectNameFor(domain: string): string {
  const d = domain.trim().toLowerCase().replace(/^www\./, "");
  const base = slugify(d.replace(/\./g, "-"), 40).replace(/^-+|-+$/g, "") || "site";
  const suffix = bytesToHex(sha256(utf8ToBytes(d))).slice(0, 6);
  return `${base}-${suffix}`;
}

async function resolveAccountId(acc: CfAccountLike): Promise<string> {
  if (acc.account_id && /^[0-9a-f]{32}$/i.test(acc.account_id)) return acc.account_id.toLowerCase();
  const list = await listAccounts(acc.api_token);
  if (!list.length) throw new Error("Token nie widzi żadnego konta CF (brak uprawnienia Account:Read?)");
  return list[0].id;
}

/** Sprawdza wszystkie konta rownolegle; bledy per konto nie blokuja reszty. */
export async function evaluateAccounts(accounts: CfAccountLike[], domain: string): Promise<AccountCandidate[]> {
  const d = domain.toLowerCase().replace(/^www\./, "");
  return Promise.all(
    accounts.map(async (account): Promise<AccountCandidate> => {
      try {
        const accountId = await resolveAccountId(account);
        const [projects, zones] = await Promise.all([
          listPagesProjects(account.api_token, accountId),
          listZones(account.api_token).catch(() => [] as CfZone[]),
        ]);
        const zone = zones.find((z) => z.name.toLowerCase() === d) || null;
        return { account, accountId, projectCount: projects.length, ownsZone: Boolean(zone), zone, error: null };
      } catch (e) {
        return { account, accountId: account.account_id || "", projectCount: -1, ownsZone: false, zone: null, error: e instanceof Error ? e.message : String(e) };
      }
    }),
  );
}

/** Najlepsze konto: ma zone i miejsce > ma miejsce i najmniej projektow. null = zadne nie pasuje. */
export function pickBest(candidates: AccountCandidate[]): AccountCandidate | null {
  const ok = candidates.filter((c) => !c.error && c.projectCount >= 0 && c.projectCount < CF_PAGES_PROJECTS_SOFT_LIMIT);
  if (!ok.length) return null;
  const withZone = ok.filter((c) => c.ownsZone).sort((a, b) => a.projectCount - b.projectCount);
  if (withZone.length) return withZone[0];
  return [...ok].sort((a, b) => a.projectCount - b.projectCount)[0];
}
